/**
 * Shareholder Graph Utilities
 *
 * Legend definitions and the small node helpers shared by the shareholder tables and the General
 * tab. The card graph (ShareholderStructure/graph) never imports from here; helpers both sides
 * need live in graph/.
 */
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { formatOwnershipShare } from '../../../../utils/ownership';
import { SHAREHOLDER_LEGEND_ICONS } from './graph/graphConstants';

/**
 * How a legend row draws its marker (ShareholderLegend renders by kind).
 */
export const SHAREHOLDER_LEGEND_KINDS = {
  ICON: 'icon',
  ULTIMATE: 'ultimate',
  CHIP: 'chip',
  DASHED: 'dashed',
  FIGURE: 'figure'
};

/**
 * Legend items, in the order of the shareholder graph mockup.
 */
export const SHAREHOLDER_LEGEND_ITEMS = [
  {
    key: 'target',
    kind: SHAREHOLDER_LEGEND_KINDS.ICON,
    icon: SHAREHOLDER_LEGEND_ICONS.target,
    label: 'Target'
  },
  {
    key: 'company',
    kind: SHAREHOLDER_LEGEND_KINDS.ICON,
    icon: SHAREHOLDER_LEGEND_ICONS.company,
    label: 'Organization'
  },
  {
    key: 'person',
    kind: SHAREHOLDER_LEGEND_KINDS.ICON,
    icon: SHAREHOLDER_LEGEND_ICONS.person,
    label: 'Individual'
  },
  {
    key: 'subsidiary',
    kind: SHAREHOLDER_LEGEND_KINDS.ICON,
    icon: SHAREHOLDER_LEGEND_ICONS.subsidiary,
    label: 'Subsidiary'
  },
  {
    key: 'ultimate',
    kind: SHAREHOLDER_LEGEND_KINDS.ULTIMATE,
    icon: SHAREHOLDER_LEGEND_ICONS.beneficial,
    label: 'Ultimate owner',
    className: 'beneficial'
  },
  { key: 'directShare', kind: SHAREHOLDER_LEGEND_KINDS.CHIP, sample: '51%', label: 'Direct share' },
  {
    key: 'aggregate',
    kind: SHAREHOLDER_LEGEND_KINDS.DASHED,
    label: 'Aggregated, hidden by filters'
  },
  {
    key: 'selfDisclosed',
    kind: SHAREHOLDER_LEGEND_KINDS.FIGURE,
    sample: '3.1',
    sampleYear: '24',
    label: 'Self-disclosed figure'
  }
];

/**
 * Format beneficial ownership value with % suffix for numeric values
 * @param {Object} entry - Shareholder entry with beneficial_ownership field
 * @returns {string} Formatted beneficial ownership string
 */
export const formatBeneficialOwnership = (entry) =>
  formatOwnershipShare(entry.beneficial_ownership) ?? PUNCTUATION.EMPTY_VALUE;

/**
 * Build detail page path for a graph node
 * @param {Object} node - Graph node object with member_id and type
 * @param {string} companyName - Current company name (for back button ref)
 * @param {Object} utilsStore - Utils store with getIndividualDetailsPath/getCompanyDetailsPath
 * @param {string|number} [companyLinkId] - Id for a company page (the live profile uuid when
 *   known); defaults to the crefo member_id. Persons always use member_id.
 * @returns {string} URL path to the node's detail page
 */
export const getNodeDetailPath = (node, companyName, utilsStore, companyLinkId) => {
  const memberId = node.member_id;
  if (node.type === 'person') {
    return utilsStore.getIndividualDetailsPath(memberId, { ref: companyName });
  }
  return utilsStore.getCompanyDetailsPath(companyLinkId || memberId, { ref: companyName });
};
